import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useFetchCgmOnboarding } from '../hooks'
import AtomicLoader from '../../../components/Loader/AtomicLoader'
import Button from '../../../components/ButtonNew'
import constants from '../../../constants'
import Text from '../../../components/Text'
import Hormones from '../../../assets/images/hormones.webp'
import Ending from '../../../assets/images/ending.webp'
import GlucoLiveLogo from '../../../assets/images/logos/glucolive.webp'
import { setActiveSupportProgramType } from '../../../store/slices/cgmSlice'

const GLUCOLIVE_CGM_TYPE = constants.CGM_TYPE.GLUCOLIVE

const LandingPage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const buttonRef = useRef(null)
  const [showStickyButton, setShowStickyButton] = useState(false)

  const [activeCgmType, setActiveCgmType] = React.useState(GLUCOLIVE_CGM_TYPE)
  const programTypeForApi = GLUCOLIVE_CGM_TYPE
  const { isLoading, error, data } = useFetchCgmOnboarding(programTypeForApi)

  const programLogo = GlucoLiveLogo

  useEffect(() => {
    setActiveCgmType(GLUCOLIVE_CGM_TYPE)
    dispatch(setActiveSupportProgramType(GLUCOLIVE_CGM_TYPE))
  }, [dispatch])

  useEffect(() => {
    if (!buttonRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyButton(!entry.isIntersecting)
      },
      {
        threshold: 0,
        rootMargin: '0px',
      },
    )

    observer.observe(buttonRef.current)

    return () => {
      if (buttonRef.current) {
        observer.unobserve(buttonRef.current)
      }
    }
  }, [data])
  const handleCtaClick = (ctaAction, redirectURL) => {
    if (ctaAction === 'signup') {
      navigate(`/signup-user?cgm=${activeCgmType}`, {
        state: {
          showHeader: true,
          showFlower: false,
          programLogo: programLogo,
          isCgmFlow: true,
        },
      })
    } else if (ctaAction === 'redirect' && redirectURL) {
      window.open(redirectURL, '_blank')
    }
  }

  const handleImageClick = (action) => {
    if (action) {
      window.open(action, '_blank')
    }
  }

  if (isLoading) {
    return <AtomicLoader show={isLoading} />
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen px-5 text-center text-red-500">
        <p>Error loading page. Please try again later.</p>
      </div>
    )
  }

  if (!data) {
    return null
  }

  const { section1, section2, section3, section4, section5 } = data?.data

  return (
    <div className="min-w-sm max-w-lg mx-auto w-full relative pb-[90px]">
      {section1 && (
        <section className="relative bg-white mb-14">
          <div className="w-full relative overflow-hidden">
            <img
              src={section1.banner?.image}
              alt="Banner"
              className="w-full h-auto object-cover"
              onClick={() =>
                handleCtaClick('redirect', section1.banner?.redirectURL)
              }
            />
          </div>
          <div className="px-4 py-2">
            <div className="mb-3 text-center">
              <Text
                variant="body"
                size="large"
                className="text-[#475467]"
                style={{
                  textAlign: 'center',
                }}
              >
                {section1?.description}
              </Text>
            </div>
            {section1?.cta && (
              <div ref={buttonRef}>
                <Button
                  children={section1?.cta?.ctaText}
                  onClick={() => handleCtaClick('signup')}
                  fullWidth={true}
                  size="small"
                  color={section1?.cta?.backgroundColor}
                />
              </div>
            )}
          </div>
        </section>
      )}

      {section2 && (
        <section className="px-4 bg-white mb-14">
          <Text
            variant="headline"
            size="small"
            className="text-text-primary"
            style={{ fontFamily: 'Playfair Display' }}
          >
            {section2?.heading}
          </Text>
          <div className="flex flex-col gap-8 mt-4">
            {section2?.steps?.map((step, index) => (
              <div key={index} className="flex gap-4 py-2">
                <div className="flex-shrink-0">
                  <div
                    className={`w-16 h-16 min-w-14 min-h-14 rounded-[20px] flex items-center justify-center text-[32px] text-white`}
                    style={{
                      backgroundColor: section2?.primaryColor,
                      boxShadow: `0px 5px 0px 0px ${section2?.secondaryColor}`,
                    }}
                  >
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1">
                  <Text
                    variant="title"
                    size="large"
                    className="text-text-primary"
                  >
                    {step?.infoTitle
                      ?.split('GoodFlip')
                      .map((part, index, array) => (
                        <React.Fragment key={index}>
                          {part}
                          {index < array.length - 1 && (
                            <img
                              src={step?.infoBanner?.goodflipImage}
                              alt="GoodFlip"
                              className="h-auto w-20 inline-block"
                            />
                          )}
                        </React.Fragment>
                      ))}
                  </Text>
                  {step?.infoBanner?.partnerImage && (
                    <div
                      className="inline-flex items-center gap-2 p-2 rounded-lg mb-1 w-full"
                      style={{
                        backgroundColor: step?.infoBanner?.backgroundColor,
                      }}
                    >
                      <Text
                        variant="body"
                        size="small"
                        className="text-text-primary"
                      >
                        {step?.infoBanner?.text}
                      </Text>
                      {step?.infoBanner?.partnerImage && (
                        <img
                          src={step?.infoBanner?.partnerImage}
                          alt="Partner"
                          className="h-auto w-12"
                        />
                      )}
                    </div>
                  )}
                  <div>
                    <Text
                      variant="body"
                      size="large"
                      className="text-text-primary"
                    >
                      {step?.infoDescription}
                    </Text>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {section3?.images && (
        <section className="px-4 bg-white mb-14">
          <Text
            variant="headline"
            size="small"
            className="text-text-primary"
            style={{ fontFamily: 'Playfair Display' }}
          >
            {section3?.heading}
          </Text>
          <div className="grid grid-cols-2 gap-4 justify-items-center mt-3">
            {section3?.images?.map((item, index) => (
              <img
                key={index}
                src={item?.image}
                alt={`Download app ${index + 1}`}
                className="h-auto w-44 cursor-pointer transition-transform duration-200 hover:scale-105"
                onClick={() => handleImageClick(item?.action)}
              />
            ))}
          </div>
        </section>
      )}

      {section4?.contactBanner && (
        <section className="bg-white mb-14">
          <div className="px-4 bg-white">
            <Text
              variant="headline"
              size="small"
              className="text-text-primary block"
              style={{ fontFamily: 'Playfair Display' }}
            >
              {' '}
              {section4?.heading}
            </Text>
            <Text variant="body" size="large" className="text-[#667085] block">
              {section4?.description}
            </Text>

            <div className="flex flex-col gap-2 mb-4 mt-2">
              {section4?.contactBanner && (
                <div
                  className="flex items-center gap-2 px-2 py-1 rounded-lg w-fit"
                  style={{
                    backgroundColor: section4?.contactBanner?.backgroundColor,
                  }}
                >
                  <img
                    src={section4?.contactBanner?.icon}
                    alt="Phone"
                    className="h-5 w-5 inline-block"
                  />
                  <Text variant="title" size="large" className="text-white">
                    {section4?.contactBanner?.label}
                    {': '}
                    {section4?.contactBanner?.contactNo}
                  </Text>
                </div>
              )}

              {section4?.emailBanner && (
                <div
                  className="flex items-center gap-2 px-2 py-1 rounded-lg w-fit"
                  style={{
                    backgroundColor: section4?.emailBanner?.backgroundColor,
                  }}
                >
                  <img
                    src={section4?.emailBanner?.icon}
                    alt="Mail"
                    className="h-5 w-5 inline-block"
                  />
                  <Text variant="title" size="large" className="text-white">
                    {section4?.emailBanner?.label}
                    {': '}
                    {section4?.emailBanner?.email}
                  </Text>
                </div>
              )}
            </div>
          </div>
          <div className="mt-5 flex-col relative h-64">
            <img
              src={Hormones}
              alt="Hormones"
              className="w-full object-cover absolute z-10 "
            />
            {section4?.image1 && (
              <div className="relative flex">
                <div>
                  <img
                    src={section4?.image1}
                    alt="Support 1"
                    className="!m-0 w-48 h-48 rounded-r-2xl object-cover absolute -left-2 !p-0"
                  />
                </div>
              </div>
            )}
            {section4?.image2 && (
              <img
                src={section4?.image2}
                alt="Support 2"
                className="w-48 h-48 rounded-l-2xl object-cover absolute -right-2 top-20 !m-0 !p-0"
              />
            )}
          </div>
        </section>
      )}

      {section5 ? (
        <section className=" bg-white mb-14">
          <div className="w-full relative overflow-hidden text-center">
            <div
              className="w-full h-40 bg-cover bg-center bg-no-repeat flex flex-col items-center justify-center"
              style={{ backgroundImage: `url(${section5?.footerImage})` }}
            >
              <p className="footer-text block">{section5?.text}</p>
              <div className="flex flex-row items-center justify-center gap-2">
                <p className="footer-subtext">{section5?.subText}</p>
                <img
                  src={section5?.icon}
                  alt="Footer"
                  className="w-12 object-contain"
                />
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-white">
          <img src={Ending} alt="Footer" className="w-full object-contain" />
        </section>
      )}

      {section1?.cta && (
        <div
          className={`fixed w-full max-w-lg mx-auto left-0 right-0 bg-white z-50 transition-all duration-300 ease-in-out ${
            showStickyButton ? 'bottom-0' : 'bottom-[-90px]'
          }`}
          style={{
            padding: '15px 16px',
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <Button
            children={section1?.cta?.ctaText}
            onClick={() => handleCtaClick('signup')}
            fullWidth={true}
            size="small"
            color={section1?.cta?.backgroundColor}
          />
        </div>
      )}
    </div>
  )
}

export default LandingPage
